import { useSectorDepotMetadata } from '@/api/useMetadata/useMasterMetadata'
import { FlagOutlined } from '@ant-design/icons'
import { Horizontal, Texto } from '@gravitate-js/excalibrr'
import { type TreeDataNode, TreeSelect } from 'antd'
import { groupBy, upperCase } from 'lodash'
import type { SelectProps } from 'antd/lib'

export type SectorDepotTreeData = {
  depots: {
    name: string
    sector: string | null
    carrier_id?: string
  }[]
}

function getSectorDepotTreeData(data: SectorDepotTreeData | undefined): TreeDataNode[] {
  if (!data?.depots) return []
  const grouped = groupBy(
    data.depots.map((depot) => ({
      ...depot,
      sector: depot.carrier_id ? 'CARRIER DEPOTS' : depot?.sector ? upperCase(depot.sector) : 'NO SECTOR',
    })),
    'sector'
  )

  return Object.entries(grouped)
    .sort(([sectorA], [sectorB]) => {
      // Display "No Sector" 2nd to last + "Carrier Depots" last
      if (sectorA === 'CARRIER DEPOTS') return 1
      if (sectorB === 'CARRIER DEPOTS') return -1
      if (sectorA === 'NO SECTOR') return 1
      if (sectorB === 'NO SECTOR') return -1
      return sectorA.localeCompare(sectorB)
    })
    .map(([sector, depots]) => {
      const isCarrierDepot = sector === 'CARRIER DEPOTS'
      return {
        title: isCarrierDepot ? (
          <Horizontal verticalCenter>
            <FlagOutlined style={{ color: 'var(--theme-color-4)' }} className='mr-2' />
            <Texto style={{ color: 'var(--theme-color-4)' }}>CARRIER DEPOTS</Texto>
          </Horizontal>
        ) : (
          sector
        ),
        sector,
        value: sector,
        key: sector,
        children: depots
          .map((d) => ({
            title: d.name,
            value: d.name,
            key: d.name,
            style: { color: isCarrierDepot ? 'var(--theme-color-4)' : 'var(--gray-800)' },
          }))
          ?.sort((a, b) => a.title?.localeCompare(b.title)),
      }
    })
}

export function SectorDepotTreeSelect({
  value,
  onChange,
  placeholder = 'All Driver Depots',
  size = 'small',
}: {
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  size?: SelectProps['size']
}) {
  const { data } = useSectorDepotMetadata()
  const treeData = getSectorDepotTreeData(data)

  return (
    <TreeSelect
      treeData={treeData}
      treeCheckable
      placeholder={placeholder}
      maxTagCount='responsive'
      style={{ width: '40%' }}
      size={size}
      onChange={onChange}
      value={value}
      allowClear
      treeDefaultExpandAll
    />
  )
}
